/**
 * useEnrollment Hook
 *
 * Manages WebAuthn PRF enrollment flow state and GraphQL operations.
 *
 * FERP-PII-052: 4-step enrollment wizard for user-controlled encryption.
 *
 * @example
 * ```tsx
 * const {
 *   isEnrolled,
 *   currentFlow,
 *   prompt,
 *   startEnrollment,
 *   completeStep,
 *   skipEnrollment
 * } = useEnrollment();
 * ```
 */

import { useState, useCallback, useEffect } from 'react';
// eslint-disable-next-line no-restricted-imports -- DATA-LAYER hook, allowed to use Apollo Client directly
import { useQuery, useMutation } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { useAuth } from '../../auth/AuthContext';

// ============================================================================
// GraphQL Operations
// ============================================================================

const IS_ENROLLED = gql`
  query BasicIsEnrolled {
    basicIsEnrolled
  }
`;

const GET_ENROLLMENT_PROMPT = gql`
  query BasicGetEnrollmentPrompt {
    basicGetEnrollmentPrompt {
      promptType
      title
      message
      priority
      canSkip
      skipReason
    }
  }
`;

const GET_CURRENT_ENROLLMENT_FLOW = gql`
  query BasicGetCurrentEnrollmentFlow {
    basicGetCurrentEnrollmentFlow {
      flowId
      status
      currentStep
      trigger
      steps {
        stepNumber
        title
        description
        status
        canSkip
        completedAt
      }
      createdAt
      updatedAt
    }
  }
`;

const START_ENROLLMENT = gql`
  mutation BasicStartEnrollment($input: BasicStartEnrollmentInput!) {
    basicStartEnrollment(input: $input) {
      flowId
      status
      currentStep
      steps {
        stepNumber
        title
        description
        status
        canSkip
      }
    }
  }
`;

const COMPLETE_ENROLLMENT_STEP = gql`
  mutation BasicCompleteEnrollmentStep($input: BasicCompleteEnrollmentStepInput!) {
    basicCompleteEnrollmentStep(input: $input) {
      flowId
      status
      currentStep
      steps {
        stepNumber
        title
        description
        status
        canSkip
        completedAt
      }
    }
  }
`;

const SKIP_ENROLLMENT_STEP = gql`
  mutation BasicSkipEnrollmentStep($input: BasicSkipEnrollmentStepInput!) {
    basicSkipEnrollmentStep(input: $input) {
      flowId
      status
      currentStep
    }
  }
`;

const SKIP_ENROLLMENT = gql`
  mutation BasicSkipEnrollment($input: BasicSkipEnrollmentInput!) {
    basicSkipEnrollment(input: $input) {
      flowId
      status
      skipReason
      remindAt
    }
  }
`;

// ============================================================================
// Types
// ============================================================================

export enum EnrollmentPromptType {
  FIRST_LOGIN = 'FIRST_LOGIN',
  ACCESS_L1_FIELD = 'ACCESS_L1_FIELD',
  TAX_SEASON = 'TAX_SEASON',
  GUARDIAN_SETUP = 'GUARDIAN_SETUP',
  BACKUP_DEVICE = 'BACKUP_DEVICE',
  RECOVERY_VERIFICATION = 'RECOVERY_VERIFICATION',
}

export enum EnrollmentStatus {
  NOT_STARTED = 'NOT_STARTED',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  SKIPPED = 'SKIPPED',
  EXPIRED = 'EXPIRED',
  ABANDONED = 'ABANDONED',
}

export enum EnrollmentStepStatus {
  PENDING = 'PENDING',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  SKIPPED = 'SKIPPED',
}

export enum SkipReason {
  REMIND_LATER = 'REMIND_LATER',
  NO_COMPATIBLE_DEVICE = 'NO_COMPATIBLE_DEVICE',
  DECLINED = 'DECLINED',
  TIMEOUT = 'TIMEOUT',
  OTHER = 'OTHER',
}

export interface EnrollmentPrompt {
  promptType: EnrollmentPromptType;
  title: string;
  message: string;
  priority: number;
  canSkip: boolean;
  skipReason?: SkipReason;
}

export interface EnrollmentStep {
  stepNumber: number;
  title: string;
  description: string;
  status: EnrollmentStepStatus;
  canSkip: boolean;
  completedAt?: string;
}

export interface EnrollmentFlow {
  flowId: string;
  status: EnrollmentStatus;
  currentStep: number;
  trigger: EnrollmentPromptType;
  steps: EnrollmentStep[];
  createdAt: string;
  updatedAt: string;
  skipReason?: SkipReason;
  remindAt?: string;
}

export interface StartEnrollmentInput {
  trigger: EnrollmentPromptType;
  userAgent?: string;
}

/**
 * Input for completing an enrollment step
 *
 * Matches backend CompleteStepInput:
 * - flow_id: String
 * - step_id: String ("primary_credential", "backup_device", "recovery_phrase", "completion")
 * - result_data: Option<String> (credential ID, recovery phrase hash, etc.)
 * - prf_output: Option<String> (base64 PRF output, required for primary_credential)
 */
export interface CompleteStepInput {
  flowId: string;
  /** Step ID: "primary_credential" | "backup_device" | "recovery_phrase" | "completion" */
  stepId: string;
  /** Result data (credential ID for WebAuthn steps) */
  resultData?: string;
  /** PRF output (base64, required for primary_credential step) */
  prfOutput?: string;
}

/** Step ID constants for type safety */
export const ENROLLMENT_STEP_IDS = {
  PRIMARY_CREDENTIAL: 'primary_credential',
  BACKUP_DEVICE: 'backup_device',
  RECOVERY_PHRASE: 'recovery_phrase',
  COMPLETION: 'completion',
} as const;

export interface SkipEnrollmentInput {
  flowId: string;
  reason: SkipReason;
}

export interface UseEnrollmentReturn {
  /** Whether user has completed enrollment */
  isEnrolled: boolean;

  /** Current enrollment flow (if any) */
  currentFlow: EnrollmentFlow | null;

  /** Enrollment prompt to show user (if not enrolled) */
  prompt: EnrollmentPrompt | null;

  /** Loading state */
  loading: boolean;

  /** Error message */
  error: string | null;

  /** Start new enrollment flow */
  startEnrollment: (trigger?: EnrollmentPromptType) => Promise<EnrollmentFlow | null>;

  /**
   * Complete current step
   *
   * @param input - Step completion input (stepId, resultData, prfOutput)
   * @example
   * // Complete primary credential step with PRF output
   * await completeStep({
   *   stepId: ENROLLMENT_STEP_IDS.PRIMARY_CREDENTIAL,
   *   resultData: credentialId,
   *   prfOutput: prfOutputBase64,
   * });
   */
  completeStep: (input: Omit<CompleteStepInput, 'flowId'>) => Promise<EnrollmentFlow | null>;

  /** Skip current step */
  skipCurrentStep: () => Promise<EnrollmentFlow | null>;

  /** Skip entire enrollment */
  skipEnrollment: (reason: SkipReason) => Promise<EnrollmentFlow | null>;

  /** Refetch enrollment status */
  refetch: () => Promise<void>;

  /** Clear error */
  clearError: () => void;
}

// ============================================================================
// Hook Implementation
// ============================================================================

export function useEnrollment(): UseEnrollmentReturn {
  const { user, isAuthenticated } = useAuth();
  const [error, setError] = useState<string | null>(null);

  // Query: Check if enrolled
  // Note: If backend doesn't have enrollment queries yet, treat as "enrolled" to skip enrollment flow
  const {
    data: enrolledData,
    loading: enrolledLoading,
    error: enrolledError,
    refetch: refetchEnrolled
  } = useQuery<Record<string, any>>(IS_ENROLLED, {
    skip: !isAuthenticated,
    fetchPolicy: 'cache-and-network',
  });

  // If enrollment query fails (not implemented), treat as enrolled to skip enrollment flow
  const effectivelyEnrolled = enrolledError ? true : (enrolledData?.basicIsEnrolled ?? false);

  // Query: Get current enrollment flow
  const {
    data: flowData,
    loading: flowLoading,
    refetch: refetchFlow
  } = useQuery<Record<string, any>>(GET_CURRENT_ENROLLMENT_FLOW, {
    skip: !isAuthenticated || effectivelyEnrolled,
    fetchPolicy: 'cache-and-network',
  });

  // Query: Get enrollment prompt
  const {
    data: promptData,
    loading: promptLoading,
  } = useQuery<Record<string, any>>(GET_ENROLLMENT_PROMPT, {
    skip: !isAuthenticated || effectivelyEnrolled,
    fetchPolicy: 'cache-and-network',
  });

  // Mutations
  const [startEnrollmentMutation, { loading: startLoading }] = useMutation<Record<string, any>>(START_ENROLLMENT);
  const [completeStepMutation, { loading: completeLoading }] = useMutation<Record<string, any>>(COMPLETE_ENROLLMENT_STEP);
  const [skipStepMutation, { loading: skipStepLoading }] = useMutation<Record<string, any>>(SKIP_ENROLLMENT_STEP);
  const [skipEnrollmentMutation, { loading: skipLoading }] = useMutation<Record<string, any>>(SKIP_ENROLLMENT);

  // Combined loading state
  const loading = enrolledLoading || flowLoading || promptLoading ||
                  startLoading || completeLoading || skipStepLoading || skipLoading;

  // Start enrollment flow
  const startEnrollment = useCallback(
    async (trigger: EnrollmentPromptType = EnrollmentPromptType.FIRST_LOGIN): Promise<EnrollmentFlow | null> => {
      setError(null);
      try {
        const { data } = await startEnrollmentMutation({
          variables: {
            input: {
              trigger,
              userAgent: navigator.userAgent,
            },
          },
        });
        return data?.basicStartEnrollment ?? null;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to start enrollment';
        setError(message);
        return null;
      }
    },
    [startEnrollmentMutation]
  );

  // Complete current step
  const completeStep = useCallback(
    async (input: Omit<CompleteStepInput, 'flowId'>): Promise<EnrollmentFlow | null> => {
      setError(null);
      const flowId = flowData?.basicGetCurrentEnrollmentFlow?.flowId;

      if (!flowId) {
        setError('No active enrollment flow');
        return null;
      }

      try {
        // Map frontend field names to backend GraphQL schema
        // Backend expects: flow_id, step_id, result_data, prf_output
        const { data } = await completeStepMutation({
          variables: {
            input: {
              flowId,
              stepId: input.stepId,
              resultData: input.resultData,
              prfOutput: input.prfOutput,
            },
          },
        });

        // Refetch enrollment status if completed
        if (data?.basicCompleteEnrollmentStep?.status === EnrollmentStatus.COMPLETED) {
          await refetchEnrolled();
        }

        return data?.basicCompleteEnrollmentStep ?? null;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to complete step';
        setError(message);
        return null;
      }
    },
    [completeStepMutation, flowData, refetchEnrolled]
  );

  // Skip current step
  const skipCurrentStep = useCallback(
    async (): Promise<EnrollmentFlow | null> => {
      setError(null);
      const flowId = flowData?.basicGetCurrentEnrollmentFlow?.flowId;

      if (!flowId) {
        setError('No active enrollment flow');
        return null;
      }

      try {
        const { data } = await skipStepMutation({
          variables: {
            input: {
              flowId,
              stepId: flowData?.basicGetCurrentEnrollmentFlow?.currentStep ?? 1,
              reason: 'user_skipped',
            },
          },
        });
        return data?.basicSkipEnrollmentStep ?? null;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to skip step';
        setError(message);
        return null;
      }
    },
    [skipStepMutation, flowData]
  );

  // Skip entire enrollment
  const skipEnrollment = useCallback(
    async (reason: SkipReason): Promise<EnrollmentFlow | null> => {
      setError(null);
      const flowId = flowData?.basicGetCurrentEnrollmentFlow?.flowId;

      if (!flowId) {
        setError('No active enrollment flow');
        return null;
      }

      try {
        const { data } = await skipEnrollmentMutation({
          variables: {
            input: { flowId, reason },
          },
        });
        return data?.basicSkipEnrollment ?? null;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to skip enrollment';
        setError(message);
        return null;
      }
    },
    [skipEnrollmentMutation, flowData]
  );

  // Refetch all enrollment data
  const refetch = useCallback(async () => {
    await Promise.all([refetchEnrolled(), refetchFlow()]);
  }, [refetchEnrolled, refetchFlow]);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    // Use effectivelyEnrolled: if query fails, treat as enrolled to skip enrollment flow
    isEnrolled: effectivelyEnrolled,
    currentFlow: flowData?.basicGetCurrentEnrollmentFlow ?? null,
    prompt: promptData?.basicGetEnrollmentPrompt ?? null,
    loading,
    error,
    startEnrollment,
    completeStep,
    skipCurrentStep,
    skipEnrollment,
    refetch,
    clearError,
  };
}
